import type { EtatRequete } from '../../api/requete.js';
import { CoqueEspace, styleLigne, styleSecondaire, styleSection } from '../clerc/commun.js';
import { BlocEstimation, type EstimationExterne } from '../clerc/estimation.js';
import { dateFr } from '../clerc/format.js';
import type { DossierNotaire, PersonneVue } from '../clerc/types.js';
import type { DonneesNotaire } from './chargement.js';

export interface PropsVueNotaire {
  etat: EtatRequete<DonneesNotaire>;
  estimation?: EstimationExterne;
}

const STATUT_ACTE: Readonly<Record<DossierNotaire['acts'][number]['status'], string>> = {
  a_preparer: 'à préparer',
  en_cours: 'en préparation',
  signe: 'signé',
  publie: 'publié',
};

const STATUT_TITRE: Readonly<Record<DossierNotaire['titles'][number]['titleStatus'], string>> = {
  trouve: 'titre trouvé',
  recherche: 'recherche en cours',
  introuvable: 'titre introuvable',
  prescription: 'prescription à examiner',
};

const nomComplet = (p: PersonneVue): string => `${p.lastName} ${p.firstNames}`.trim();

function Acte({ acte }: { acte: DossierNotaire['acts'][number] }) {
  return (
    <li style={styleLigne}>
      <span>{acte.label}</span>
      <span style={styleSecondaire}>
        {STATUT_ACTE[acte.status]}
        {acte.dueAt ? ` · avant le ${dateFr(acte.dueAt)}` : ''}
      </span>
    </li>
  );
}

export function VueEspaceNotaire({ etat, estimation }: PropsVueNotaire) {
  return (
    <CoqueEspace
      ecran="X12"
      titre="Espace notaire"
      etat={etat}
      vide="Ouvrez votre dossier depuis le lien reçu du Cabinet MAAT. Si vous ne le retrouvez pas, contactez le cabinet."
      enfants={({ dossier, pieces }) => {
        const aFaire = dossier.acts.filter((a) => a.status === 'a_preparer' || a.status === 'en_cours');
        const obtenus = dossier.acts.filter((a) => a.status === 'signe' || a.status === 'publie');
        return (
          <>
            <h2>{dossier.title}</h2>
            <p style={styleSecondaire}>Dossier {dossier.reference} · suivi avec le Cabinet MAAT</p>

            <section aria-labelledby="not-actes" style={styleSection}>
              <h3 id="not-actes">État des actes</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {aFaire.map((a, i) => (
                  <Acte key={a.label + i} acte={a} />
                ))}
              </ul>
              {aFaire.length === 0 ? <p>Aucun acte n’est en attente.</p> : null}
            </section>

            <section aria-labelledby="not-obtenus" style={styleSection}>
              <h3 id="not-obtenus">Actes déjà obtenus</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {obtenus.map((a, i) => (
                  <Acte key={a.label + i} acte={a} />
                ))}
              </ul>
              {obtenus.length === 0 ? <p>Aucun acte obtenu pour l’instant.</p> : null}
            </section>

            <section aria-labelledby="not-devolution" style={styleSection}>
              <h3 id="not-devolution">Dévolution</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th scope="col">Héritier</th>
                    <th scope="col">Qualité</th>
                    <th scope="col">Identité</th>
                    <th scope="col">Situation</th>
                  </tr>
                </thead>
                <tbody>
                  {dossier.living.map((p, i) => (
                    <tr key={nomComplet(p) + i}>
                      <th scope="row">{nomComplet(p)}</th>
                      <td>{p.relationLabel ?? 'qualité à préciser'}</td>
                      <td>{p.identityVerified ? 'identité vérifiée' : 'identité à vérifier'}</td>
                      <td>{p.isHypothesis ? 'en réserve, recherches en cours' : 'établi'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section aria-labelledby="not-titres" style={styleSection}>
              <h3 id="not-titres">Titres de propriété</h3>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {dossier.titles.map((t, i) => (
                  <li key={t.assetLabel + i} style={styleLigne}>
                    <span>{t.assetLabel}</span>
                    <span style={styleSecondaire}>{STATUT_TITRE[t.titleStatus]}</span>
                  </li>
                ))}
              </ul>
            </section>

            {pieces.length > 0 ? (
              <section aria-labelledby="not-pieces" style={styleSection}>
                <h3 id="not-pieces">Pièces du dossier</h3>
                <ul>
                  {pieces.map((p, i) => (
                    <li key={p.title + i}>{p.title}</li>
                  ))}
                </ul>
              </section>
            ) : null}

            {estimation ? <BlocEstimation role="notaire" reglage={estimation.reglage} lignes={estimation.lignes} /> : null}

            <section aria-labelledby="not-interlocuteur" style={styleSection}>
              <h3 id="not-interlocuteur">Votre interlocuteur</h3>
              <p>Cabinet MAAT</p>
              <p style={styleSecondaire}>Mandataire des héritiers, procurations au dossier</p>
            </section>

            <p style={styleSecondaire}>Accès en lecture. Les notes internes et les échanges avec les familles ne sont pas visibles.</p>
          </>
        );
      }}
    />
  );
}
